import { useAuth } from "@/features/auth/hooks/useAuth";
import { usePlan } from "@/features/auth/hooks/usePlan";
import { YeatiesTermsDialog } from "@/features/purchasePoints/components/YeatiesTermsDialog";
import { useYeatiesConsent } from "@/features/purchasePoints/hooks/useYeatiesConsent";
import { canBeAskedAboutMesYeaties } from "@/features/purchasePoints/utils/yeatiesConsentEligibility";

// Kept apart from the gate so that its state, an error included, lives until the page unmounts.
const YeatiesConsentPrompt = () => {
  const {
    accept, refuse, pendingAnswer, consentError,
  } = useYeatiesConsent();

  // A dialog that cannot be dismissed must not trap him behind a server failure.
  if (consentError !== null) return null;

  return (
    <YeatiesTermsDialog
      open
      isBlocking
      onAccept={accept}
      onDecline={refuse}
      isPending={pendingAnswer !== null}
      isDeclining={pendingAnswer === "refuse"}
    />
  );
};

YeatiesConsentPrompt.displayName = "YeatiesConsentPrompt";

type YeatiesConsentGateProps = {
  // The card onboarding asks the same question: never both at once.
  isCardOnboardingPending: boolean
};

/**
 * Asks the employee still undecided about Mes Yeaties on the pages he lands on.
 */
export const YeatiesConsentGate = ({ isCardOnboardingPending }: YeatiesConsentGateProps) => {
  const { user } = useAuth();
  const plan = usePlan();
  const employee = user?.employee;

  const shouldAsk = canBeAskedAboutMesYeaties(user, plan)
    // Strict: undefined is a session still loading, false a refusal already stored.
    && employee?.allow_purchase_points === null
    // Never activated: the question is asked in the card activation flow.
    && employee?.card_ever_activated !== false
    && !isCardOnboardingPending;

  return shouldAsk ? <YeatiesConsentPrompt /> : null;
};

YeatiesConsentGate.displayName = "YeatiesConsentGate";
